import { Link } from "react-router-dom";
import { Sparkles, Zap, GraduationCap, Trophy, ArrowRight, Brain } from "lucide-react";

export default function Landing() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
      {/* HERO */}
      <section className="grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-7 space-y-6 pop-in">
          <span className="nb-badge nb-tag-sun" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" /> Adapts to every learner
          </span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tighter">
            Learn <span className="bg-[var(--sun)] px-2 rounded-lg border-2 border-black">what you don't know</span>,<br />
            skip <span className="italic">what you do.</span>
          </h1>
          <p className="text-lg sm:text-xl text-neutral-700 max-w-xl">
            Knacky finds your weak points across Math, Science and English — then serves the exact next question at the exact right difficulty. Powered by Claude, GPT and Gemini.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/signup" data-testid="hero-cta" className="nb-btn text-base px-6 py-3">
              Start learning free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/for-schools" data-testid="hero-schools-cta" className="nb-btn nb-btn-secondary text-base px-6 py-3">
              For schools
            </Link>
          </div>
          <div className="flex gap-4 pt-4 text-sm text-neutral-600">
            <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-[var(--coral)]" /> Adaptive difficulty</span>
            <span className="flex items-center gap-1.5"><Brain className="w-4 h-4 text-[var(--mint)]" /> AI tutor 24/7</span>
            <span className="flex items-center gap-1.5"><Trophy className="w-4 h-4 text-[var(--sun)]" /> Mastery paths</span>
          </div>
        </div>

        <div className="md:col-span-5 space-y-4">
          <div className="nb-card p-5 rotate-1">
            <img
              src="https://images.unsplash.com/photo-1543269664-02e941c052f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwbGVhcm5pbmclMjBsYXB0b3AlMjBoYXBweXxlbnwwfHx8fDE3ODU3Mjk0NjJ8MA&ixlib=rb-4.1.0&q=85"
              alt="Student learning"
              className="w-full h-56 object-cover rounded-lg border-2 border-black"
            />
            <div className="pt-3 flex justify-between items-center">
              <div>
                <div className="text-xs uppercase font-bold text-neutral-500">Live session</div>
                <div className="font-bold">Algebra Basics · Level 3</div>
              </div>
              <span className="nb-badge nb-tag-mint">92% mastery</span>
            </div>
          </div>
          <div className="nb-card p-5 -rotate-1 bg-[var(--sun)]">
            <div className="font-bold text-sm">Weak point detected</div>
            <div className="text-2xl font-extrabold">Linear Equations 📉</div>
            <div className="text-sm mt-1">Serving 5 targeted drills now →</div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="mt-24 grid md:grid-cols-12 gap-6">
        <FeatureCard className="md:col-span-5 bg-white" title="Weak-point radar" body="We map every attempt onto a mastery graph. When mastery dips, the next question doubles down there — not on stuff you've aced." tag="Radar" tagCls="nb-tag-coral" />
        <FeatureCard className="md:col-span-7 bg-[var(--bg-highlight)]" title="An AI tutor that actually explains" body="Ask 'why?' as many times as you want. Claude Sonnet, GPT and Gemini break down each step, adapting tone to the student's grade and pace." tag="AI Tutor" tagCls="nb-tag-sky" />
        <FeatureCard className="md:col-span-4 bg-[var(--mint)]" title="Pace, not race" body="Sessions adjust length and difficulty to keep you in the sweet-spot — where learning actually happens." tag="Adaptive" tagCls="nb-tag-sun" />
        <FeatureCard className="md:col-span-4 bg-white" title="Parents & teachers see it all" body="One dashboard. Every skill. Every gap. Every win." tag="Overview" tagCls="nb-tag-coral" />
        <FeatureCard className="md:col-span-4 bg-[var(--sun)]" title="Schools: seat-based licensing" body="Roster upload, cohort analytics and per-student mastery — priced per seat." tag="For Schools" tagCls="nb-tag-sky" />
      </section>

      {/* CTA */}
      <section className="mt-24 nb-card p-10 sm:p-16 bg-[var(--coral)] text-white text-center">
        <h2 className="text-4xl sm:text-5xl font-extrabold">Ready to learn smarter?</h2>
        <p className="max-w-xl mx-auto mt-3 text-white/90">Sign up free — no card, no fluff. Upgrade only when you love it.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/signup" className="nb-btn nb-btn-secondary" data-testid="footer-cta-signup"><GraduationCap className="w-4 h-4" /> Start free</Link>
          <Link to="/pricing" className="nb-btn nb-btn-accent" data-testid="footer-cta-pricing">See plans</Link>
        </div>
      </section>

      <footer className="mt-16 py-8 text-center text-sm text-neutral-500">
        © {new Date().getFullYear()} Knacky · Adaptive tutoring for humans
      </footer>
    </div>
  );
}

function FeatureCard({ className = "", title, body, tag, tagCls }) {
  return (
    <div className={`nb-card nb-card-lift p-6 ${className}`}>
      <span className={`nb-badge ${tagCls}`}>{tag}</span>
      <h3 className="text-2xl font-extrabold mt-3">{title}</h3>
      <p className="text-neutral-700 mt-2">{body}</p>
    </div>
  );
}
